// src/pages/ManageStationaryTransaction.jsx
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SummaryApi from "../../../common";

export default function ManageStationaryTransaction() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const stationary = state?.stationary;

  const [transactions, setTransactions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    type: "",
    installments: [
      { installmentNumber: 1, amount: "", paidOn: "", status: "Pending" },
    ],
  });

  // ✅ Fetch transactions for this stationary
  const fetchTransactions = async () => {
    try {
      const res = await fetch(SummaryApi.stationaryTransactions.getAll.url, {
        method: SummaryApi.stationaryTransactions.getAll.method,
      });
      const data = await res.json();
      const filtered = data.filter((t) => t.stationaryId?._id === id);
      setTransactions(filtered);
    } catch (err) {
      console.error("Error fetching transactions:", err);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  // ✅ Helpers for total validation
  const getTotalAmount = () =>
    form.installments.reduce((sum, inst) => sum + Number(inst.amount || 0), 0);

  const getExistingTotal = () =>
    transactions.reduce(
      (sum, t) =>
        sum + t.installments.reduce((s, i) => s + Number(i.amount || 0), 0),
      0
    );

  // ✅ Handle installment change
  const handleInstallmentChange = (index, e) => {
    const newInstallments = [...form.installments];
    newInstallments[index][e.target.name] = e.target.value;
    setForm({ ...form, installments: newInstallments });
  };

  // ✅ Add new installment
  const addInstallment = () => {
    const currentTotal = getExistingTotal() + getTotalAmount();
    const remaining = stationary.amount - currentTotal;

    if (remaining <= 0) {
      setError(
        `You cannot create more installments. The total ₹${stationary.amount} has been reached.`
      );
      return;
    }

    setForm({
      ...form,
      installments: [
        ...form.installments,
        {
          installmentNumber: form.installments.length + 1,
          amount: "",
          paidOn: "",
          status: "Pending",
        },
      ],
    });
    setError("");
  };

  // ✅ Create new transaction
  const handleSubmit = async (e) => {
    e.preventDefault();
    const existingTotal = getExistingTotal();
    const newTotal = getTotalAmount() + existingTotal;

    if (newTotal > stationary.amount) {
      const remaining = stationary.amount - existingTotal;
      setError(
        `You cannot create installments exceeding ₹${stationary.amount}. You can only add ₹${remaining} more.`
      );
      return;
    }

    try {
      await fetch(SummaryApi.stationaryTransactions.create.url, {
        method: SummaryApi.stationaryTransactions.create.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, stationaryId: id }),
      });

      setShowModal(false);
      setForm({
        type: "",
        installments: [
          { installmentNumber: 1, amount: "", paidOn: "", status: "Pending" },
        ],
      });
      setError("");
      fetchTransactions();
    } catch (err) {
      console.error("Error creating transaction:", err);
    }
  };

  // ✅ Update installment status
  const handleEditInstallmentStatus = async (
    transactionId,
    instIndex,
    newStatus
  ) => {
    try {
      const transaction = transactions.find((t) => t._id === transactionId);
      const installmentId = transaction.installments[instIndex]._id;

      const url = `${SummaryApi.stationaryTransactions.update(transactionId).url}/installment/${installmentId}`;

      await fetch(url, {
        method: SummaryApi.stationaryTransactions.update(transactionId).method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });

      setEditingStatus(null);
      fetchTransactions();
    } catch (error) {
      console.error("Edit error:", error);
    }
  };

  // ✅ Delete transaction
  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm("Are you sure you want to delete this transaction?"))
      return;

    try {
      const { url, method } =
        SummaryApi.stationaryTransactions.delete(transactionId);
      await fetch(url, { method });
      fetchTransactions();
    } catch (error) {
      console.error("Delete error:", error);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">
            Manage Transactions – {stationary?.schoolName}
          </h1>
          <p className="text-gray-500">
            Project: {stationary?.projectName} | Total Amount: ₹{stationary?.amount}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
          >
            ← Back
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow"
          >
            + Add Transaction
          </button>
        </div>
      </div>

      {/* Transaction Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full text-sm text-left text-gray-700">
          <thead className="bg-gray-100 text-gray-800 uppercase text-xs">
            <tr>
              <th className="px-4 py-3">#</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Overall Status</th>
              <th className="px-4 py-3">Installments</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {transactions.length ? (
              transactions.map((t, idx) => (
                <tr key={t._id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3">{idx + 1}</td>
                  <td className="px-4 py-3">{t.type}</td>
                  <td className="px-4 py-3 font-medium">
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        t.overallStatus === "Paid"
                          ? "bg-green-100 text-green-800"
                          : t.overallStatus === "Partial"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {t.overallStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <ul className="list-disc list-inside space-y-1">
                      {t.installments.map((inst, i) => (
                        <li key={i} className="flex justify-between items-center gap-2">
                          <span>
                            #{inst.installmentNumber} – ₹{inst.amount} on{" "}
                            {inst.paidOn?.slice(0, 10)} ({inst.status})
                          </span>
                          <div className="flex gap-1 items-center">
                            {editingStatus?.id === t._id &&
                            editingStatus?.index === i ? (
                              <>
                                <select
                                  className="border rounded px-1 text-sm"
                                  defaultValue={inst.status}
                                  onChange={(e) =>
                                    handleEditInstallmentStatus(t._id, i, e.target.value)
                                  }
                                >
                                  <option value="Pending">Pending</option>
                                  <option value="Paid">Paid</option>
                                </select>
                                <button
                                  className="text-gray-500 text-xs ml-1"
                                  onClick={() => setEditingStatus(null)}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => setEditingStatus({ id: t._id, index: i })}
                                className="text-blue-600 text-xs hover:underline"
                              >
                                Edit
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleDeleteTransaction(t._id)}
                      className="text-red-600 text-sm hover:underline"
                    >
                      Delete Transaction
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center py-4 text-gray-500">
                  No transactions yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-lg rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Add Transaction</h2>

            {error && (
              <div className="bg-red-100 text-red-800 p-2 rounded mb-2 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
              <select
                name="type"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                required
                className="w-full border px-3 py-2 rounded-md"
              >
                <option value="">Select Type</option>
                <option value="Stationary">Stationary</option>
              </select>

              {form.installments.map((inst, idx) => (
                <div key={idx} className="border rounded-md p-3 bg-gray-50">
                  <div className="font-medium text-gray-600 mb-2">
                    Installment #{inst.installmentNumber}
                  </div>
                  <input
                    type="number"
                    name="amount"
                    value={inst.amount}
                    onChange={(e) => handleInstallmentChange(idx, e)}
                    placeholder="Amount"
                    required
                    className="w-full border px-3 py-2 rounded-md mb-2"
                  />
                  <input
                    type="date"
                    name="paidOn"
                    value={inst.paidOn}
                    onChange={(e) => handleInstallmentChange(idx, e)}
                    className="w-full border px-3 py-2 rounded-md mb-2"
                  />
                  <select
                    name="status"
                    value={inst.status}
                    onChange={(e) => handleInstallmentChange(idx, e)}
                    className="w-full border px-3 py-2 rounded-md"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Paid">Paid</option>
                  </select>
                </div>
              ))}

              <button
                type="button"
                onClick={addInstallment}
                className="text-blue-600 hover:underline text-sm"
              >
                + Add Installment
              </button>

              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
